import { useEffect, useState, useRef } from "react";
import { useFormik } from "formik";
import { Form, Row, Col } from "react-bootstrap";
import MDEditor, { commands } from "@uiw/react-md-editor";
import "@uiw/react-md-editor/markdown-editor.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faPen, faNoteSticky, faPlus, faFaceSmile } from "@fortawesome/free-solid-svg-icons";
import Picker from "@emoji-mart/react";
import emojiData from "@emoji-mart/data";
import crudApi from "@serie3/common/api/crudApi";
import MediaplannerGroupEndpoints from "@serie3/mymediaplanner/Modules/Groups/endpoints";
import GroupNoteForm from "@serie3/mymediaplanner/Modules/Groups/Forms/GroupNoteForm";
import NewModal from "../../../../shared/Modals/NewModal";
import SaveButton from "../../../../shared/Buttons/SaveButton";
import TextField from "../../../../Form/TextField";

const formatDate = (raw) => {
  if (!raw) return null;
  const d = new Date(raw);
  if (isNaN(d)) return null;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

const GroupNotesTab = ({ group }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!group?.id) return;
    setLoading(true);
    crudApi
      .justGet(MediaplannerGroupEndpoints.endpoints.notes.list + group.id)
      .then((r) => { setNotes(r.ok && Array.isArray(r.data?.notes) ? r.data.notes : []); })
      .finally(() => setLoading(false));
  }, [group?.id]);

  const noteForm = GroupNoteForm.createGroupNoteForm(notes, setNotes, group?.id, setShowModal);
  const formik = useFormik(noteForm);

  const openNew = () => {
    formik.resetForm();
    setShowModal(true);
  };

  const openEdit = (note) => {
    formik.setValues(GroupNoteForm.setEditData(note));
    setShowModal(true);
  };

  const deleteNote = async (noteId) => {
    const response = await crudApi.del(
      MediaplannerGroupEndpoints.endpoints.notes.delete,
      noteId
    );
    if (response.ok || response.status === 204) {
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    }
  };

  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const emojiCommand = {
    name: "emoji",
    keyCommand: "emoji",
    buttonProps: { "aria-label": "Insert emoji" },
    icon: <FontAwesomeIcon icon={faFaceSmile} style={{ fontSize: 13 }} />,
    execute: () => setShowEmojiPicker((v) => !v),
  };

  const insertEmoji = (emoji) => {
    const native = emoji.native;
    formik.setFieldValue("content", (formik.values.content ?? "") + native);
    setShowEmojiPicker(false);
  };

  const Save = () => <SaveButton formik={formik} />;

  return (
    <>
      <NewModal
        show={showModal}
        setShow={setShowModal}
        title={formik.values.id > 0 ? "Edit note" : "New note"}
        ExtraButton={Save}
        onExit={() => formik.resetForm()}
        size="xl"
      >
        <Form onSubmit={formik.handleSubmit}>
          <Row className="mb-3">
            <Col>
              <TextField formik={formik} label="Title" valueName="title" />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Label style={{ fontSize: 12, fontWeight: 600, color: "#495057" }}>
                Content
              </Form.Label>
              <div data-color-mode="light" style={{ fontFamily: "Montserrat, sans-serif", position: "relative" }}>
                <MDEditor
                  value={formik.values.content}
                  onChange={(val) => formik.setFieldValue("content", val ?? "")}
                  height={420}
                  preview="edit"
                  style={{ fontFamily: "Montserrat, sans-serif" }}
                  previewOptions={{ style: { fontFamily: "Montserrat, sans-serif" } }}
                  commands={[
                    commands.title1, commands.title2, commands.title3,
                    commands.divider,
                    commands.bold, commands.italic,
                    commands.divider,
                    commands.link,
                    commands.divider,
                    emojiCommand,
                  ]}
                  extraCommands={[]}
                />
                {showEmojiPicker && (
                  <div style={{ position: "absolute", zIndex: 1050, marginTop: 4 }}>
                    <Picker data={emojiData} onEmojiSelect={insertEmoji} theme="light" />
                  </div>
                )}
              </div>
            </Col>
            <Col md={6}>
              <Form.Label style={{ fontSize: 12, fontWeight: 600, color: "#495057" }}>
                Preview
              </Form.Label>
              <div
                data-color-mode="light"
                style={{
                  fontFamily: "Montserrat, sans-serif",
                  height: 420, overflowY: "auto",
                  border: "1px solid #e9ecef", borderRadius: 6,
                  padding: "12px 16px", background: "#fafbfc",
                }}
              >
                <MDEditor.Markdown
                  source={formik.values.content || "*Nothing to preview yet…*"}
                  style={{ fontFamily: "Montserrat, sans-serif", background: "transparent" }}
                />
              </div>
            </Col>
          </Row>
        </Form>
      </NewModal>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <FontAwesomeIcon icon={faNoteSticky} style={{ color: "#564ab1", fontSize: 16 }} />
          <span style={{ fontWeight: 600, fontSize: 15, color: "#1a2236" }}>Notes</span>
          {notes.length > 0 && (
            <span style={{
              fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
              background: "rgba(86,74,177,0.12)", color: "#564ab1",
            }}>
              {notes.length}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={openNew}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "6px 14px", borderRadius: 8, border: "none", cursor: "pointer",
            background: "linear-gradient(135deg, #564ab1, #3d3490)",
            color: "#fff", fontSize: 12, fontWeight: 600,
            boxShadow: "0 2px 8px rgba(86,74,177,0.25)",
          }}
        >
          <FontAwesomeIcon icon={faPlus} style={{ fontSize: 11 }} />
          New note
        </button>
      </div>

      {loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton" style={{ height: 80, borderRadius: 10 }} />
          ))}
        </div>
      )}

      {!loading && notes.length === 0 && (
        <div style={{ textAlign: "center", padding: "48px 0", color: "#9299a8" }}>
          <FontAwesomeIcon icon={faNoteSticky} style={{ fontSize: 36, marginBottom: 12, opacity: 0.2 }} />
          <p style={{ fontSize: 14, fontWeight: 600, color: "#374151", marginBottom: 4 }}>No notes yet</p>
          <p style={{ fontSize: 13 }}>Add a note to share information with the group.</p>
        </div>
      )}

      {!loading && notes.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {notes.map((note) => (
            <div
              key={note.id}
              style={{
                background: "#fff", border: "1px solid #eef0f3",
                borderLeft: "3px solid #564ab1", borderRadius: 10,
                padding: "14px 16px", display: "flex", flexDirection: "column", gap: 6,
              }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: "#1a2236" }}>
                  {note.title || <em style={{ color: "#9299a8", fontWeight: 400 }}>Untitled</em>}
                </span>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button
                    type="button"
                    onClick={() => openEdit(note)}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#9299a8", fontSize: 12, padding: "2px 4px", transition: "color 0.15s" }}
                    onMouseEnter={(e) => e.currentTarget.style.color = "#564ab1"}
                    onMouseLeave={(e) => e.currentTarget.style.color = "#9299a8"}
                  >
                    <FontAwesomeIcon icon={faPen} />
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteNote(note.id)}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#9299a8", fontSize: 12, padding: "2px 4px", transition: "color 0.15s" }}
                    onMouseEnter={(e) => e.currentTarget.style.color = "#e74c3c"}
                    onMouseLeave={(e) => e.currentTarget.style.color = "#9299a8"}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
              {note.content && (
                <div data-color-mode="light" style={{ fontSize: 13, fontFamily: "Montserrat, sans-serif" }}>
                  <MDEditor.Markdown
                    source={note.content}
                    style={{ fontFamily: "Montserrat, sans-serif" }}
                  />
                </div>
              )}
              {note.created && (
                <span style={{ fontSize: 11, color: "#adb5bd" }}>{formatDate(note.created)}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default GroupNotesTab;
